import { IAssignee, IAssigneesStoreState } from "@gtmhub/assignees";
import { IStateInit } from "@gtmhub/core/routing";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IdMap } from "@gtmhub/util";
import { StateService, TransitionService } from "@uirouter/angularjs";
import { IPromise, IQService, IRootScopeService, ITimeoutService } from "angular";
import { IModalScope, IModalService, IModalServiceInstance } from "angular-ui-bootstrap";
import { INotification, INotificationIndicators, IMetricNotificationDataItem, TargetType } from "../models/notifications";
import { NotificationsService } from "../services/notificationsService";
import { INgRedux } from "@gtmhub/state-management";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";

interface INotificationsInboxData {
  indicators: INotificationIndicators;
  newMessagesCount: number;
}

type IExpandableNotification = INotification & {
  expanded: boolean;
};

interface INotificationsScope extends IModalScope {
  pagingSize: number;
  page: number;
  messages: IExpandableNotification[];
  indicators: INotificationIndicators;
  newMessageAvailable: boolean;
  newMessagesCount: number;
  assigneesMap: IdMap<IAssignee>;
  loadInbox(): IPromise<void>;
  markAll(): void;
  showMoreMessages(): void;
  showMessage(index: number): void;
  closeInbox(): void;
}

export class NotificationsInboxService {
  public static $inject = ["$rootScope", "$uibModal"];

  constructor(private $rootScope: IGtmhubRootScopeService, private $uibModal: IModalService) {}

  public open(indicators: INotificationIndicators, newMessagesCount: number): void {
    this.$uibModal.open({
      templateUrl: require("../views/inbox.jade"),
      windowClass: this.generateWindowClass(),
      controller: "NotificationsInboxCtrl",
      resolve: {
        inboxData: (): INotificationsInboxData => ({
          indicators,
          newMessagesCount,
        }),
      },
    });
  }

  private generateWindowClass(): string {
    const positionRelativeToHiddenV1Nav = this.$rootScope.hideMenu ? " nav-v1-hidden" : "";
    const positionNewExp = this.$rootScope.newExperienceEnabledForUser ? " nav-v2" : "";
    return "sidebar-modal inbox-modal large right" + positionRelativeToHiddenV1Nav + positionNewExp;
  }
}

export class NotificationsInboxCtrl implements IStateInit {
  static $inject = [
    "$rootScope",
    "$transitions",
    "$scope",
    "$state",
    "$uibModalInstance",
    "$timeout",
    "$q",
    "NotificationsService",
    "UIErrorHandlingService",
    "inboxData",
    "$ngRedux",
  ];
  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $transitions: TransitionService,
    private $scope: INotificationsScope,
    private $state: StateService,
    private $uibModalInstance: IModalServiceInstance,
    private $timeout: ITimeoutService,
    private $q: IQService,
    private notificationsService: NotificationsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private inboxData: INotificationsInboxData,
    private $ngRedux: INgRedux
  ) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init(): IPromise<void> {
    this.$scope.pagingSize = 50;
    this.$scope.indicators = this.inboxData.indicators;
    this.$scope.newMessagesCount = this.inboxData.newMessagesCount;

    this.addEventListeners();

    this.$scope.loadInbox = (): IPromise<void> => {
      return this.$rootScope.traceAction("load_inbox", () => {
        this.$scope.newMessageAvailable = false;

        this.$scope.indicators.loading = true;
        return this.notificationsService.getNotifications(null, null, null, null, 0, this.$scope.pagingSize).then(
          (notifications) => {
            this.$scope.page = 1;
            this.$scope.messages = notifications.items as IExpandableNotification[];
            this.$timeout(() => {
              const el = document.getElementById("inbox-messages");
              if (el) {
                el.scrollTo({ top: 0 });
              }
            }, 1000);
            this.$scope.indicators.loading = false;
            this.notificationTextForMultipleAssignees();
          },
          (err) => {
            this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" });
          }
        );
      });
    };

    this.$scope.markAll = () => {
      this.$rootScope.traceAction("mark_all_as_seen", () => {
        this.$scope.indicators.markingAllAsSeen = true;
        this.notificationsService.markAllAsSeen().then(
          () => {
            for (let i = 0; i < this.$scope.messages.length; i++) {
              this.$scope.messages[i].seen = true;
            }
            this.updateNewMessagesCount("markingAllAsSeen");
          },
          (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "markingAllAsSeen" })
        );
      });
    };

    this.$scope.showMoreMessages = () => {
      this.$rootScope.traceAction("show_more_messages", () => {
        this.$scope.indicators.loadingMoreMessages = true;
        this.notificationsService.getNotifications(null, null, null, null, this.$scope.page * this.$scope.pagingSize, this.$scope.pagingSize).then(
          (notifications) => {
            // takes height of inbox-messages container with old messages
            const inboxEl = document.getElementById("inbox-messages");
            const msgsHeight = inboxEl.scrollHeight - 70;
            this.$scope.messages = this.$scope.messages.concat(notifications.items as IExpandableNotification[]);
            this.$scope.indicators.loadingMoreMessages = false;

            this.$timeout(() => {
              // takes height of inbox-messages container with old and new messages
              const newMsgsHeight = inboxEl.scrollHeight;

              // animates the loading and display of the new messages
              this.$timeout(() => inboxEl.scrollTo({ top: msgsHeight }), 1000);

              const coverEl = document.createElement("div");
              coverEl.id = "new-msg-cover";
              coverEl.classList.add("new-messages-cover");
              inboxEl.appendChild(coverEl);

              coverEl.style.height = `${newMsgsHeight - msgsHeight}px`;
              coverEl.style.top = `${msgsHeight}px`;

              this.$timeout(() => {
                coverEl.classList.add("shown");

                this.$timeout(() => {
                  coverEl.classList.remove("shown");
                }, 500);

                this.$timeout(() => {
                  coverEl.remove();
                }, 1000);
              }, 500);
            }, 1);

            this.$scope.page++;
          },
          (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "showingMoreMessages" })
        );
      });
    };

    this.$scope.showMessage = (index) => {
      this.$rootScope.traceAction("show_message", () => {
        for (let i = 0; i < this.$scope.messages.length; i++) {
          if (index !== i) {
            this.$scope.messages[i].expanded = false;
          }
        }
        this.$scope.messages[index].expanded = !this.$scope.messages[index].expanded;

        if (!this.$scope.messages[index].seen) {
          this.$scope.indicators.markingAsSeen = true;
          this.notificationsService.markAsSeen(this.$scope.messages[index].id).then(
            () => {
              this.$scope.messages[index].seen = true;
              this.updateNewMessagesCount("markingAsSeen");
            },
            (err) => {
              this.$scope.indicators.markingAsSeen = false;
              this.uiErrorHandlingService.handleModal(err);
            }
          );
        }
      });
    };

    this.$scope.closeInbox = () => {
      this.$uibModalInstance.close();
    };

    // used for team avatars
    const unsubscribe = this.$ngRedux.connect(this.mapStateToScope.bind(this))(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    return this.$scope.loadInbox();
  }

  private getNewMessageCount = (): IPromise<number> => {
    return this.notificationsService.getNotifications(null, null, null, false, 0, 1).then(
      (notifications) => notifications.totalCount,
      (err) => {
        this.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" });
        return this.$q.reject(err);
      }
    );
  };

  private updateNewMessagesCount = (indicatorType: string) => {
    this.getNewMessageCount().then((count) => {
      this.$scope.newMessagesCount = count;
      this.$rootScope.$broadcast("newMessagesCount", count);

      if (indicatorType === "markingAllAsSeen") {
        this.$scope.indicators.markingAllAsSeen = false;
      } else if (indicatorType === "markingAsSeen") {
        this.$scope.indicators.markingAsSeen = false;
      }
    });
  };

  private addEventListeners() {
    this.$scope.$on("notificationsCount", () => {
      this.$timeout(() => {
        this.$scope.newMessageAvailable = true;
      });

      this.$timeout(() => {
        this.$scope.newMessageAvailable = false;
      }, 3000);
    });

    this.$scope.$on(
      "$destroy",
      this.$transitions.onSuccess({}, () => {
        const stateName = this.$state.current.name;

        if (stateName.includes("insight")) {
          this.$scope.$close();
        }
      })
    );
  }

  private mapStateToScope(state: IAssigneesStoreState) {
    const assignees = state.assignees;
    return {
      assigneesMap: assignees.map,
    };
  }

  private notificationTextForMultipleAssignees() {
    for (let i = 0; i < this.$scope.messages.length; i++) {
      const isMetricType = this.$scope.messages[i].targetType == TargetType.MetricType;
      const metricNotificationDataItem = this.$scope.messages[i].notificationData.item as IMetricNotificationDataItem;
      const nonNullStruct =
        this.$scope.messages[i].notificationData != null && this.$scope.messages[i].notificationData.item != null && metricNotificationDataItem.assignees != null;

      if (isMetricType && nonNullStruct && metricNotificationDataItem.assignees.length == 1) {
        metricNotificationDataItem.templatedNotificationTextTranslated = localize("new_kr_inside_your_okr_assigned_to_single_owner", {
          assigneeName: metricNotificationDataItem.assignees[0].names,
        });
      }
      if (isMetricType && nonNullStruct && metricNotificationDataItem.assignees.length == 2) {
        metricNotificationDataItem.templatedNotificationTextTranslated = localize("new_kr_inside_your_okr_assigned_to_two_owners", {
          assigneeName: metricNotificationDataItem.assignees[0].names,
        });
      }
      if (isMetricType && nonNullStruct && metricNotificationDataItem.assignees.length > 2) {
        metricNotificationDataItem.templatedNotificationTextTranslated = localize("new_kr_inside_your_okr_assigned_to_two_plus_owners", {
          assigneeName: metricNotificationDataItem.assignees[0].names,
          numberOfLeftAssignees: metricNotificationDataItem.assignees.length - 1,
        });
      }
    }
  }
}
